import { ThemeConfig } from 'src/app/@data-services/event-config/event-theme-config.data-model';
import { DEFAULT_ROZIE_THEME } from './default-theme.config';
import { deriveHoverHex } from './derive-hover-hex';

export const applyTheme = (config?: ThemeConfig): void => {
  const root = document.querySelector(':root') as HTMLElement;

  if (root) {
    // Use provided config, fallback to Rozie default theme
    const themeToApply = config || DEFAULT_ROZIE_THEME;

    const setProperty = (
      property: string,
      defaultValue: string,
      value?: string | undefined
    ): void => {
      root.style.setProperty(property, value || defaultValue);
    };

    // Fallback background: 50% white overlay atop a primary-gradient tint
    // (start → start/end mix → end), each mixed with transparent for opacity.
    const fallbackBackgroundImage = [
      'linear-gradient(rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.2)),',
      'linear-gradient(135deg,',
      'color-mix(in srgb, var(--primary-gradient-start) 10%, transparent) 0%,',
      'color-mix(in srgb, color-mix(in srgb,' +
        ' var(--primary-gradient-start), var(--primary-gradient-end))' +
        ' 20%, transparent) 38.9%,',
      'color-mix(in srgb, var(--primary-gradient-end) 20%, transparent) 70.72%)',
    ].join(' ');

    const properties = [
      {
        property: '--theme-background-image',
        defaultValue: DEFAULT_ROZIE_THEME.Light.BackgroundImage,
        value: config?.Light?.BackgroundImage?.trim()
          ? `url('${config.Light.BackgroundImage.trim()}')`
          : fallbackBackgroundImage,
      },
      {
        property: '--primary-gradient-start',
        defaultValue: DEFAULT_ROZIE_THEME.Light.Colors.PrimaryGradientStart,
        value: themeToApply?.Light?.Colors?.PrimaryGradientStart,
      },
      {
        property: '--primary-gradient-end',
        defaultValue: DEFAULT_ROZIE_THEME.Light.Colors.PrimaryGradientEnd,
        value: themeToApply?.Light?.Colors?.PrimaryGradientEnd,
      },
      {
        property: '--primary-gradient-start-hover',
        defaultValue: deriveHoverHex(
          DEFAULT_ROZIE_THEME.Light.Colors.PrimaryGradientStart
        ),
        value: themeToApply?.Light?.Colors?.PrimaryGradientStart
          ? deriveHoverHex(themeToApply.Light.Colors.PrimaryGradientStart)
          : undefined,
      },
      {
        property: '--primary-gradient-end-hover',
        defaultValue: deriveHoverHex(
          DEFAULT_ROZIE_THEME.Light.Colors.PrimaryGradientEnd
        ),
        value: themeToApply?.Light?.Colors?.PrimaryGradientEnd
          ? deriveHoverHex(themeToApply.Light.Colors.PrimaryGradientEnd)
          : undefined,
      },
      {
        property: '--primary-color-variant-2',
        defaultValue: DEFAULT_ROZIE_THEME.Light.Colors.PrimaryColorVariant2,
        value: themeToApply?.Light?.Colors?.PrimaryColorVariant2,
      },
      {
        property: '--card-gradient-hover-start',
        defaultValue: DEFAULT_ROZIE_THEME.Light.Colors.CardGradientHoverStart,
        value: themeToApply?.Light?.Colors?.CardGradientHoverStart,
      },
      {
        property: '--card-gradient-hover-end',
        defaultValue: DEFAULT_ROZIE_THEME.Light.Colors.CardGradientHoverEnd,
        value: themeToApply?.Light?.Colors?.CardGradientHoverEnd,
      },
      {
        property: '--primary-background-patch-left-top',
        defaultValue: DEFAULT_ROZIE_THEME.Light.Colors.BackgroundPatchLeftTop,
        value: themeToApply?.Light?.Colors?.BackgroundPatchLeftTop,
      },
      {
        property: '--primary-background-patch-left-middle',
        defaultValue:
          DEFAULT_ROZIE_THEME.Light.Colors.BackgroundPatchLeftMiddle,
        value: themeToApply?.Light?.Colors?.BackgroundPatchLeftMiddle,
      },
      {
        property: '--primary-background-patch-right-middle',
        defaultValue:
          DEFAULT_ROZIE_THEME.Light.Colors.BackgroundPatchRightMiddle,
        value: themeToApply?.Light?.Colors?.BackgroundPatchRightMiddle,
      },
      {
        property: '--primary-background-patch-gradient-bottom-start',
        defaultValue:
          DEFAULT_ROZIE_THEME.Light.Colors.BackgroundPatchGradientBottomStart,
        value: themeToApply?.Light?.Colors?.BackgroundPatchGradientBottomStart,
      },
      {
        property: '--primary-background-patch-gradient-bottom-end',
        defaultValue:
          DEFAULT_ROZIE_THEME.Light.Colors.BackgroundPatchGradientBottomEnd,
        value: themeToApply?.Light?.Colors?.BackgroundPatchGradientBottomEnd,
      },
      // Card colors
      {
        property: '--session-insight-card-header-gradient-start',
        defaultValue:
          DEFAULT_ROZIE_THEME.Light.Colors
            .SessionInsightCardHeaderGradientStart!,
        value:
          config?.Light?.Colors?.SessionInsightCardHeaderGradientStart ||
          config?.Light?.Colors?.PrimaryGradientStart,
      },
      {
        property: '--session-insight-card-header-gradient-end',
        defaultValue:
          DEFAULT_ROZIE_THEME.Light.Colors.SessionInsightCardHeaderGradientEnd!,
        value:
          config?.Light?.Colors?.SessionInsightCardHeaderGradientEnd ||
          config?.Light?.Colors?.PrimaryGradientEnd,
      },
      // Session state gradient colors
      {
        property: '--session-upcoming-gradient-start',
        defaultValue:
          DEFAULT_ROZIE_THEME.Light.Colors.SessionUpcomingGradientStart!,
        value:
          themeToApply?.Light?.Colors?.SessionUpcomingGradientStart ||
          config?.Light?.Colors?.PrimaryGradientStart,
      },
      {
        property: '--session-upcoming-gradient-end',
        defaultValue:
          DEFAULT_ROZIE_THEME.Light.Colors.SessionUpcomingGradientEnd!,
        value:
          themeToApply?.Light?.Colors?.SessionUpcomingGradientEnd ||
          config?.Light?.Colors?.PrimaryGradientEnd,
      },
      {
        property: '--session-live-gradient-start',
        defaultValue:
          DEFAULT_ROZIE_THEME.Light.Colors.SessionLiveGradientStart!,
        value:
          themeToApply?.Light?.Colors?.SessionLiveGradientStart ||
          config?.Light?.Colors?.PrimaryGradientStart,
      },
      {
        property: '--session-live-gradient-end',
        defaultValue: DEFAULT_ROZIE_THEME.Light.Colors.SessionLiveGradientEnd!,
        value:
          themeToApply?.Light?.Colors?.SessionLiveGradientEnd ||
          config?.Light?.Colors?.PrimaryGradientEnd,
      },
      {
        property: '--session-replay-gradient-start',
        defaultValue:
          DEFAULT_ROZIE_THEME.Light.Colors.SessionReplayGradientStart!,
        value:
          themeToApply?.Light?.Colors?.SessionReplayGradientStart ||
          config?.Light?.Colors?.PrimaryGradientStart,
      },
      {
        property: '--session-replay-gradient-end',
        defaultValue:
          DEFAULT_ROZIE_THEME.Light.Colors.SessionReplayGradientEnd!,
        value:
          themeToApply?.Light?.Colors?.SessionReplayGradientEnd ||
          config?.Light?.Colors?.PrimaryGradientEnd,
      },
      {
        property: '--session-debrief-gradient-start',
        defaultValue:
          DEFAULT_ROZIE_THEME.Light.Colors.SessionDebriefGradientStart!,
        value:
          themeToApply?.Light?.Colors?.SessionDebriefGradientStart ||
          config?.Light?.Colors?.PrimaryGradientStart,
      },
      {
        property: '--session-debrief-gradient-end',
        defaultValue:
          DEFAULT_ROZIE_THEME.Light.Colors.SessionDebriefGradientEnd!,
        value:
          themeToApply?.Light?.Colors?.SessionDebriefGradientEnd ||
          config?.Light?.Colors?.PrimaryGradientEnd,
      },
      // Bottom sheet accent (button + checkbox)
      {
        property: '--bottom-sheet-accent-gradient-start',
        defaultValue:
          DEFAULT_ROZIE_THEME.Light.Colors.BottomSheetAccentGradientStart!,
        value:
          themeToApply?.Light?.Colors?.BottomSheetAccentGradientStart ||
          config?.Light?.Colors?.PrimaryGradientStart,
      },
      {
        property: '--bottom-sheet-accent-gradient-end',
        defaultValue:
          DEFAULT_ROZIE_THEME.Light.Colors.BottomSheetAccentGradientEnd!,
        value:
          themeToApply?.Light?.Colors?.BottomSheetAccentGradientEnd ||
          config?.Light?.Colors?.PrimaryGradientEnd,
      },
      // Session agenda tab colors. The redesigned agenda uses a custom ink-bar
      // that reads --primary-gradient-* directly, so only the hover background
      // is still consumed; the selected gradient/background vars were removed
      // along with their (now-deleted) dead _mat-tab.scss consumer.
      {
        property: '--session-agenda-tab-hover-background',
        defaultValue:
          DEFAULT_ROZIE_THEME.Light.Colors.SessionAgendaTabHoverBackground!,
        value: config?.Light?.Colors?.SessionAgendaTabHoverBackground,
      },
      {
        property: '--overlay-opacity',
        defaultValue: DEFAULT_ROZIE_THEME.Light.ShowOverlay ? '1' : '0',
        value: themeToApply?.Light?.ShowOverlay === true ? '1' : '0',
      },
    ];

    properties.forEach(({ property, defaultValue, value }) => {
      setProperty(property, defaultValue, value);
    });
  }
};
